import { Link, useLocation } from 'react-router-dom';
import { Home, Library, BarChart3, HelpCircle, Settings, Building, Users, ChevronLeft, ChevronRight, X, ClipboardList, Brain, Wrench } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useSidebar } from '@/contexts/SidebarContext';

interface SidebarProps {
  isAdmin: boolean;
}

export function Sidebar({ isAdmin }: SidebarProps) {
  const location = useLocation();
  const { isCollapsed, toggleSidebar } = useSidebar();

  const mainNavItems = [
    { icon: Home, label: 'Home', href: '/' },
    ...(isAdmin ? [
      { icon: Building, label: 'Organizations', href: '/organizations' },
      { icon: Users, label: 'Users', href: '/users' },
    ] : []),
    { icon: ClipboardList, label: 'Surveys', href: '/surveys' },
    { icon: Brain, label: 'Strategy', href: '/strategy' },
    { icon: Wrench, label: 'Tools', href: '/tools' },
    { icon: Library, label: 'Library', href: '/library' },
    { icon: BarChart3, label: 'Reports', href: '/reports' },
  ];

  const bottomNavItems = [
    { icon: HelpCircle, label: 'Support', href: '/support' },
    { icon: Settings, label: 'Settings', href: '/settings' },
  ];

  return (
    <aside 
      className={cn(
        "bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col fixed h-screen transition-all duration-300 z-30",
        isCollapsed ? "w-16" : "w-64",
        "lg:translate-x-0",
        isCollapsed ? "-translate-x-full lg:translate-x-0" : "translate-x-0"
      )}
    >
      {/* Close button for mobile */}
      <button
        onClick={toggleSidebar}
        className="lg:hidden absolute right-4 top-4 text-gray-500 dark:text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
      >
        <X className="h-6 w-6" />
      </button>

      {/* Logo Section */}
      <div className="h-16 flex items-center px-4 border-b border-gray-200 dark:border-gray-700">
        <img
          src="/aipc-final.svg"
          alt="Logo"
          className="h-8 dark:invert"
        />
      </div>
      
      {/* Main Navigation */}
      <div className="flex-1 flex flex-col">
        <nav className="flex-1 px-2 py-4">
          <div className="space-y-1">
            {mainNavItems.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className={cn(
                  "flex items-center px-3 py-2 rounded-lg transition-colors",
                  "hover:bg-gray-100 dark:hover:bg-gray-700",
                  location.pathname === item.href && "bg-gray-100 dark:bg-gray-700",
                  isCollapsed ? "justify-center" : "space-x-3"
                )}
                onClick={() => {
                  if (window.innerWidth < 1024) {
                    toggleSidebar();
                  }
                }}
              >
                <item.icon className={cn(
                  "h-5 w-5",
                  location.pathname === item.href
                    ? "text-blue-600 dark:text-blue-400"
                    : "text-gray-500 dark:text-gray-400"
                )} />
                {!isCollapsed && (
                  <span className={cn(
                    "text-sm font-medium",
                    location.pathname === item.href
                      ? "text-blue-600 dark:text-blue-400"
                      : "text-gray-700 dark:text-gray-300"
                  )}>
                    {item.label}
                  </span>
                )}
              </Link>
            ))}
          </div>
        </nav>

        {/* Collapse Button - Only show on desktop */}
        <div className="hidden lg:block px-2 py-2 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={toggleSidebar}
            className={cn(
              "w-full flex items-center px-3 py-2 rounded-lg transition-colors",
              "hover:bg-gray-100 dark:hover:bg-gray-700",
              isCollapsed ? "justify-center" : "justify-end"
            )}
          >
            {isCollapsed ? (
              <ChevronRight className="h-5 w-5 text-gray-500 dark:text-gray-400" />
            ) : (
              <ChevronLeft className="h-5 w-5 text-gray-500 dark:text-gray-400" />
            )}
          </button>
        </div>

        {/* Bottom Navigation */}
        <div className="px-2 py-4 border-t border-gray-200 dark:border-gray-700">
          <div className="space-y-1">
            {bottomNavItems.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className={cn(
                  "flex items-center px-3 py-2 rounded-lg transition-colors",
                  "hover:bg-gray-100 dark:hover:bg-gray-700",
                  location.pathname === item.href && "bg-gray-100 dark:bg-gray-700",
                  isCollapsed ? "justify-center" : "space-x-3"
                )}
                onClick={() => {
                  if (window.innerWidth < 1024) {
                    toggleSidebar();
                  }
                }}
              >
                <item.icon className={cn(
                  "h-5 w-5",
                  location.pathname === item.href
                    ? "text-blue-600 dark:text-blue-400"
                    : "text-gray-500 dark:text-gray-400"
                )} />
                {!isCollapsed && (
                  <span className={cn(
                    "text-sm font-medium",
                    location.pathname === item.href
                      ? "text-blue-600 dark:text-blue-400"
                      : "text-gray-700 dark:text-gray-300"
                  )}>
                    {item.label}
                  </span>
                )}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </aside>
  );
}